import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const fmtMoney = (n) => {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "GBP" }).format(n || 0);
  } catch {
    return `£${(n || 0).toFixed(2)}`;
  }
};

const BUCKETS = [
  { key: "current_due", label: "Current", description: "Not yet due", color: "bg-emerald-50 border-emerald-300 text-emerald-900" },
  { key: "overdue_1_30_days", label: "1–30 days", description: "Slightly overdue", color: "bg-amber-50 border-amber-300 text-amber-900" },
  { key: "overdue_31_60_days", label: "31–60 days", description: "Action required", color: "bg-orange-50 border-orange-300 text-orange-900" },
  { key: "overdue_60_plus_days", label: "60+ days", description: "Escalate", color: "bg-rose-50 border-rose-400 text-rose-900" },
];

const AgingReport = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  // Memoise the sorted invoice list so we don't re-sort on every keystroke / poll tick.
  const sortedInvoices = useMemo(
    () => (report?.invoices || []).slice().sort(
      (a, b) => (b.days_overdue || 0) - (a.days_overdue || 0)
    ),
    [report]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices/aging");
      setReport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load aging report");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-12 text-sm text-gray-500">Loading aging report…</div>;
  if (!report) return null;

  const maxBucket = Math.max(
    report.current_due,
    report.overdue_1_30_days,
    report.overdue_31_60_days,
    report.overdue_60_plus_days,
    1
  );

  return (
    <div>
      <PageHeader
        eyebrow="Finance · A/R"
        title="Aging Report"
        description={`Generated ${new Date(report.generated_at).toLocaleString()} · ${report.invoice_count} open invoice${report.invoice_count === 1 ? "" : "s"}`}
        actions={
          <Link to="/invoices">
            <Button variant="outline" className="rounded-none border-gray-900" data-testid="back-to-invoices">
              <ArrowLeft className="w-4 h-4 mr-1" /> Invoices
            </Button>
          </Link>
        }
      />

      {/* Bucket cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[#1F2937] border-b border-[#1F2937]">
        {BUCKETS.map((b) => {
          const value = report[b.key] || 0;
          const pct = (value / maxBucket) * 100;
          return (
            <div key={b.key} className="bg-[#0D1117] px-6 py-5" data-testid={`bucket-${b.key}`}>
              <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500">{b.label}</div>
              <div className="font-display font-black text-3xl tracking-tighter mt-1 font-mono" data-testid={`bucket-value-${b.key}`}>{fmtMoney(value)}</div>
              <div className="text-[11px] text-gray-500 mt-0.5">{b.description}</div>
              <div className="h-1 bg-[#111827] mt-3 relative">
                <div
                  className={`absolute top-0 left-0 h-full ${b.color.split(" ")[0].replace("50", "500")}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Total banner */}
      <div className="bg-gray-900 text-white px-5 sm:px-8 lg:px-12 py-5 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400">Total outstanding</div>
          <div className="font-display font-black text-4xl tracking-tighter mt-1 font-mono" data-testid="total-outstanding">{fmtMoney(report.total_outstanding)}</div>
        </div>
        {report.overdue_60_plus_days > 0 && (
          <div className="flex items-center gap-2 text-rose-300 text-sm">
            <AlertTriangle className="w-4 h-4" strokeWidth={1.7} />
            <span><strong className="font-mono">{fmtMoney(report.overdue_60_plus_days)}</strong> over 60 days — escalate.</span>
          </div>
        )}
      </div>

      {/* Breakdown table */}
      <div className="px-5 sm:px-8 lg:px-12 py-8">
        <h2 className="text-xs uppercase tracking-[0.25em] text-gray-500 mb-3">Invoice breakdown</h2>
        {(report.invoices || []).length === 0 ? (
          <div className="border border-dashed border-[#374151] p-12 text-center text-sm text-gray-500">
            Nothing outstanding — you&apos;re fully collected.
          </div>
        ) : (
          <div className="border border-[#1F2937]">
            <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#1F2937] bg-[#030712] text-[10px] uppercase tracking-[0.25em] text-gray-500">
              <div className="col-span-2">Number</div>
              <div className="col-span-3">Client</div>
              <div className="col-span-2">Due</div>
              <div className="col-span-2 text-right">Days overdue</div>
              <div className="col-span-2 text-right">Balance</div>
              <div className="col-span-1 text-right">Bucket</div>
            </div>
            {sortedInvoices.map((inv) => {
                const bucket = BUCKETS.find((b) => b.key === inv.bucket);
                return (
                  <Link
                    key={inv.invoice_id}
                    to={`/invoices/${inv.invoice_id}`}
                    className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#1F2937] hover:bg-[#030712] items-center text-sm"
                    data-testid={`aging-row-${inv.invoice_id}`}
                  >
                    <div className="col-span-2 font-mono text-xs">{inv.invoice_number}</div>
                    <div className="col-span-3 truncate">{inv.client_name || "—"}</div>
                    <div className="col-span-2 font-mono text-xs text-gray-500">
                      {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : "—"}
                    </div>
                    <div className={`col-span-2 text-right font-mono ${inv.days_overdue > 30 ? "text-rose-700 font-semibold" : "text-gray-500"}`}>
                      {inv.days_overdue > 0 ? `${inv.days_overdue}d` : "—"}
                    </div>
                    <div className="col-span-2 text-right font-mono font-semibold">{fmtMoney(inv.balance_due)}</div>
                    <div className="col-span-1 text-right">
                      <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${bucket?.color || "border-[#1F2937] bg-[#030712]"}`}>
                        {bucket?.label || inv.bucket}
                      </span>
                    </div>
                  </Link>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgingReport;
